import { useHistory } from 'react-router-dom'
import { useMemo, useState } from 'react'

import { useSignInWithSocialMutation } from '@sketch/gql-types'

import { useAsync } from 'react-use'
import {
  useAnalytics,
  useQueryParams,
  useSetUserAuthorization,
  routes,
} from '@sketch/modules-common'
import { useToast } from '@sketch/toasts'
import { localStorageKeys } from '@sketch/constants'
import { getItem } from '@sketch/utils'
import { ErrorHandler } from '@sketch/tracing'

type Loading = { type: 'loading' }
type Error = { type: 'error' }

type Success = { type: 'success'; from?: string }
type SuccessDeleteAccount = { type: 'success-delete-account' }
type SuccessDeleteWorkspace = { type: 'success-delete-workspace' }
type SuccessWorkspaceSubscribe = {
  type: 'success-workspace-subscribe'
  socialWorkspaceId: string
}

export type State =
  | Loading
  | Success
  | SuccessDeleteAccount
  | SuccessDeleteWorkspace
  | SuccessWorkspaceSubscribe
  | Error

export const useSocialSuccessfulSignIn = () => {
  const history = useHistory()
  const { showToast } = useToast()
  const { trackEvent } = useAnalytics()

  const [state, setState] = useState<State>({ type: 'loading' })
  const queryParams = useQueryParams<'SOCIAL_SUCCESSFUL_SIGN_IN'>()

  const setUserAuthorization = useSetUserAuthorization()

  const [signInWithSocialMutation] = useSignInWithSocialMutation({
    onError: () => {
      ErrorHandler.shouldNeverHappen('Social sign in failed')
      showToast('Something went wrong', 'negative')
      setState({ type: 'error' })
    },
    onCompleted: async data => {
      // MFA auth
      if (data.signInWithSocial.__typename === 'MFACredentials') {
        history.push(routes.VERIFICATION_CODE.create({}), {
          mfaToken: data.signInWithSocial.mfaToken,
          from: queryParams.from,
          socialWorkspaceId: queryParams.workspace,
        })
        return
      }

      setUserAuthorization(data.signInWithSocial.credentials)

      // If the user signed in / signed up via the Campaign flow (/signin?source=pricing OR /signup?source=pricing)
      // and the user did NOT have a workspace, one will be created for them automatically.
      // In this case, we want to redirect the user to the Workspace Subscription page
      if (queryParams.workspace) {
        setState({
          type: 'success-workspace-subscribe',
          socialWorkspaceId: queryParams.workspace,
        })
      }

      setState({ type: 'success', from: queryParams.from })

      trackEvent('Successful SignIn', { type: 'social' })
    },
  })

  const signInWithSocial = useMemo(
    () => (exchangeToken: string) => {
      const socialState = getItem(localStorageKeys.socialState) ?? ''

      signInWithSocialMutation({
        variables: {
          exchangeToken,
          state: socialState,
        },
      })
    },
    [signInWithSocialMutation]
  )

  useAsync(async () => {
    if (state.type !== 'loading') return

    try {
      // User deleted his account by clicking "Delete Account" in the User Settings page
      // After authentication with social provider, BE redirects to success page with deleteAccount query param
      // in this case, we just sign out the user
      if ('deleteAccount' in queryParams) {
        showToast('Account deleted successfully', 'positive')

        setState({ type: 'success-delete-account' })
        return
      }

      // User deleted his workspace by clicking "Delete Workspace" in the Delete Workspace modal
      // After authentication with social provider, BE redirects to success page with deleteWorkspace query param
      // in this case, we redirect the user to the Entry page
      if ('deleteWorkspace' in queryParams) {
        showToast('Workspace deleted successfully', 'positive')

        setState({ type: 'success-delete-workspace' })
        return
      }

      if (!queryParams.token) {
        showToast('An error occurred, please try again', 'negative')
        ErrorHandler.shouldNeverHappen('No token found')

        setState({ type: 'error' })
        return
      }

      signInWithSocial(queryParams.token!)
    } catch (error) {
      setState({ type: 'error' })
    }
  }, [state, queryParams.token])

  return { state }
}
